import { useState } from "react";
import { lovable } from "@/integrations/lovable/index";
import { A } from "@/lib/authStyles";

type Props = {
  label?: string;
  onError?: (msg: string) => void;
  /**
   * Optional override. When provided, replaces the default OAuth flow
   * (used by callers that need to validate / persist state before the
   * Google redirect). The override is responsible for initiating the
   * OAuth call itself.
   */
  onClick?: () => Promise<void> | void;
};

/**
 * Translate raw OAuth/Supabase error strings into clear, actionable
 * inline messages for the user. Covers the most common Google sign-in
 * failure modes: misconfigured provider, blocked / cancelled flow,
 * domain restrictions, and account-already-exists conflicts.
 */
export function friendlyGoogleError(raw: string): string {
  const m = (raw || "").toLowerCase();

  // Provider not configured / disabled in Lovable Cloud.
  if (
    m.includes("provider is not enabled") ||
    m.includes("provider not enabled") ||
    m.includes("unsupported provider") ||
    m.includes("oauth provider") && m.includes("disabl")
  ) {
    return "Google sign-in isn't enabled yet. Please contact support or try email sign-in.";
  }

  // Misconfigured client / redirect URI / invalid credentials on the OAuth app.
  if (
    m.includes("redirect_uri") ||
    m.includes("invalid client") ||
    m.includes("invalid_client") ||
    m.includes("misconfigur") ||
    m.includes("oauth configuration") ||
    m.includes("client id")
  ) {
    return "Google sign-in is misconfigured. Please contact support — our team has been notified.";
  }

  // Hosted-domain restriction (Google Workspace `hd` param) or blocked domain.
  if (
    m.includes("hd ") ||
    m.includes("hosted domain") ||
    m.includes("domain not allowed") ||
    m.includes("blocked domain") ||
    m.includes("not authorized") && m.includes("domain")
  ) {
    return "Your Google account's domain isn't allowed. Try a different account or use email sign-in.";
  }

  // Account already exists with a different sign-in method.
  if (
    m.includes("already registered") ||
    m.includes("already exists") ||
    m.includes("user already") ||
    m.includes("identity is already") ||
    m.includes("email address is already") ||
    m.includes("account exists")
  ) {
    return "An account with this email already exists. Sign in with your email and password, then link Google from Settings.";
  }

  // User cancelled or closed the popup / blocked third-party cookies.
  if (
    m.includes("popup") ||
    m.includes("closed by user") ||
    m.includes("cancelled") ||
    m.includes("canceled") ||
    m.includes("access_denied") ||
    m.includes("user denied")
  ) {
    return "Google sign-in was cancelled. Please try again.";
  }

  // Network / connectivity.
  if (m.includes("network") || m.includes("failed to fetch") || m.includes("timeout")) {
    return "Network issue while contacting Google — check your connection and try again.";
  }

  // Rate limiting.
  if (m.includes("rate limit") || m.includes("too many requests")) {
    return "Too many sign-in attempts. Please wait a moment and try again.";
  }

  return raw || "Couldn't sign in with Google. Please try again.";
}

/**
 * "Continue with Google" button using the Lovable Cloud managed OAuth broker.
 * On success the browser is redirected to Google; the auth listener in
 * AuthContext handles the post-redirect session and role lookup.
 */
export function GoogleSignInButton({ label = "Continue with Google", onError, onClick }: Props) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    if (onClick) {
      try {
        await onClick();
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e ?? "");
        onError?.(friendlyGoogleError(raw));
        setLoading(false);
      }
      return;
    }
    try {
      const redirectUri = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: redirectUri,
      });

      if (result.error) {
        const raw =
          result.error instanceof Error ? result.error.message : String(result.error ?? "");
        onError?.(friendlyGoogleError(raw));
        setLoading(false);
        return;
      }
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e ?? "");
      onError?.(friendlyGoogleError(raw));
      setLoading(false);
      return;
    }
    // If result.redirected, the browser is navigating away — keep the
    // spinner state so the button doesn't flash back to idle.
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      style={{
        width: "100%",
        background: "#fff",
        color: "#1f1f1f",
        border: `1px solid ${A.border}`,
        padding: "12px 16px",
        borderRadius: 12,
        fontFamily: "Outfit, 'DM Sans', sans-serif",
        fontWeight: 700,
        fontSize: 14,
        cursor: loading ? "default" : "pointer",
        opacity: loading ? 0.65 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        transition: "opacity 120ms ease",
      }}
    >
      <GoogleGlyph />
      {loading ? "Connecting…" : label}
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.7 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
